<!-- [팝업] 컨텐츠 유형 선택 -->
<template>
  <b-modal
    v-model="showContentSelect"
    cancel-variant="outline-secondary"
    centered
    size="lg"
    title="컨텐츠 유형 선택"
    no-close-on-esc
    no-close-on-backdrop
    @close.prevent="close()"
  >
    <b-row class="mt-1">
      <b-table
        ref="refInterestContentTable"
        primary-key="id"
        :items="fetchData"
        :fields="currentTableColumns"
        :sort-by.sync="sortBy"
        :sort-desc.sync="isSortDesc"
        responsive
        sticky-header
        class="position-relative"
      >
        <template #cell(select)="data">
          <div
            v-show="!isEditMode"
            style="text-align: center;"
          >
            <b-form-checkbox
              :id="`content-checkbox-${data.item.id}`"
              v-model="selectedContentIds"
              :value="data.item.id"
              name="content-checkbox"
            />
          </div>
        </template>
        <template #cell(title)="data">
          <label
            v-show="!isEditMode"
            :for="`content-checkbox-${data.item.id}`"
            class="content-label"
          >
            <div>{{ data.item.title }}</div>
            <small>{{ data.item.description }}</small>
          </label>
          <div v-show="isEditMode">
            <div>{{ data.item.title }}</div>
            <small>{{ data.item.description }}</small>
          </div>
        </template>
        <template #cell(edit)="data">
          <div
            v-show="isEditMode"
          >
            <span
              class="badge badge-light-primary"
              @click.prevent="edit(data.item)"
            > 수정 </span>
          </div>
        </template>
        <template #cell(remove)="data">
          <div
            v-show="isEditMode"
          >
            <span
              class="badge badge-light-danger"
              @click.prevent="remove(data.item)"
            > 삭제 </span>
          </div>
        </template>
      </b-table>
    </b-row>
    <interest-content-edit
      :is-new="interestContentIsNew"
      :edit-item="interestContentItem"
      :show-interest-content-edit="showInterestContentEdit"
      @close="interestContentEdit.close"
    />
    <template #modal-footer>
      <b-button
        v-show="!isEditMode"
        variant="outline-primary"
        class="mr-auto"
        @click="toggleEditMode(true)"
      >
        항목설정
      </b-button>
      <b-button
        v-show="isEditMode"
        variant="outline-primary"
        class="mr-auto"
        @click="toggleEditMode(false)"
      >
        돌아가기
      </b-button>
      <b-button
        v-show="!isEditMode"
        variant="outline-secondary"
        @click="close"
      >
        취소
      </b-button>
      <b-button
        v-show="!isEditMode"
        variant="primary"
        :disabled="selectedContentIds.length === 0"
        @click="submit"
      >
        적용
      </b-button>
      <b-button
        v-show="isEditMode"
        variant="primary"
        @click="add"
      >
        추가
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import axios from '@axios'
import {
  ref, onMounted, getCurrentInstance, computed,
} from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { errorFormatter } from '@core/utils/filter'
import { cloneDeep } from 'lodash'
import InterestContentEdit from '@/views/apps/contents/interest-content/InterestContentEdit.vue'

export default {
  components: {
    InterestContentEdit,
  },

  props: {
    showContentSelect: {
      type: Boolean,
      required: true,
    },
    selectedValue: {
      type: String,
      required: false,
      default: null,
    },
  },

  setup(props, { emit }) {
    onMounted(() => {
    })

    const toast = useToast()
    const instance = getCurrentInstance()
    const bvModal = instance.proxy.$bvModal
    const pushToast = (variant, title) => {
      let icon
      if (variant === 'success') {
        icon = 'CheckCircleIcon'
      } else {
        icon = 'AlertTriangleIcon'
      }
      toast({
        component: ToastificationContent,
        props: { title, icon, variant },
      })
    }

    const selectedContentIds = ref([])
    const loadedItems = ref([])
    const isSelectionRestored = ref(false)
    const isEditMode = ref(false)
    // const isRefetch = ref(false)

    // Main Table Config
    const refInterestContentTable = ref(null)
    const sortBy = ref('createDate')
    const isSortDesc = ref(false)

    const currentTableColumns = computed(() => (isEditMode.value ? editTableColumns : selectTableColumns))

    const selectTableColumns = [
      {
        key: 'select', label: '선택', sortable: false, thStyle: { width: '11%', textAlign: 'center' },
      },
      {
        key: 'title', label: '항목', sortable: false, thStyle: { width: 'auto' },
      },
    ]

    const editTableColumns = [
      {
        key: 'title', label: '항목', sortable: false, thStyle: { width: 'auto' },
      },
      {
        key: 'edit', label: '수정', sortable: false, thStyle: { width: '11%' },
      },
      {
        key: 'remove', label: '삭제', sortable: false, thStyle: { width: '11%' },
      },
    ]
    // Main Table Config End.

    const fetchData = (ctx, callback) => {
      axios.post('/fa/interest-content/list', {
        search: {},
        sort: {
          predicate: sortBy.value,
          reverse: isSortDesc.value,
        },
      })
        .then(rs => {
          const { items } = rs.data
          callback(items)
          loadedItems.value = items

          if (!isSelectionRestored.value) {
            isSelectionRestored.value = true
            if (props.selectedValue) {
              selectedContentIds.value = props.selectedValue
                .split(',')
                .map(id => Number(id.trim()))
                .filter(id => !Number.isNaN(id) && items.some(item => item.id === id))
            }
          } else {
            selectedContentIds.value = selectedContentIds.value.filter(id => items.some(item => item.id === id))
          }
        })
        .catch(error => {
          pushToast('danger', errorFormatter(error, "데이터 조회에 실패하였습니다."))
        })
    }

    const refetchData = () => {
      refInterestContentTable.value.refresh()
    }

    const interestContentItem = ref(null)
    const interestContentIsNew = ref(false)
    const showInterestContentEdit = ref(false)
    const interestContentEdit = {
      close: rs => {
        showInterestContentEdit.value = false
        if (rs) {
          // isRefetch.value = true
          refetchData()
        }
      },
    }

    const add = () => {
      interestContentItem.value = null
      interestContentIsNew.value = true
      showInterestContentEdit.value = true
    }

    const edit = item => {
      interestContentItem.value = cloneDeep(item)
      interestContentIsNew.value = false
      showInterestContentEdit.value = true
    }

    const remove = item => {
      bvModal
        .msgBoxConfirm("삭제하시겠습니까?", {
          size: "sm",
          cancelVariant: "outline-secondary",
          centered: true,
          okTitle: '확인',
          cancelTitle: '취소',
        })
        .then(confirm => {
          if (!confirm) return

          axios
            .delete(`/fa/interest-content/${item.id}`)
            .then(() => {
              pushToast('success', '데이터 삭제 완료')
              // isRefetch.value = true
              refetchData()
            })
            .catch(error => {
              pushToast('danger', errorFormatter(error, "삭제하는 과정에서 오류가 발생하였습니다."))
            })
        })
    }

    const toggleEditMode = mode => {
      isEditMode.value = mode

      if (mode) {
        refInterestContentTable.value.clearSelected()
      } else {
        refetchData()
      }
    }

    const close = () => {
      emit('close', null)
      clear()
    }

    const submit = () => {
      if (selectedContentIds.value.length === 0) {
        pushToast('warning', '컨텐츠를 선택하세요')
        return
      }
      const selected = loadedItems.value
        .filter(item => selectedContentIds.value.includes(item.id))
        .sort((a, b) => a.id - b.id)
        .map(item => ({ id: item.id, title: item.title }))

      emit('close', selected)
      clear()
    }

    const clear = () => {
      isEditMode.value = false
      showInterestContentEdit.value = false
      selectedContentIds.value = []
      isSelectionRestored.value = false
    }

    return {
      add,
      edit,
      remove,
      close,
      submit,
      refInterestContentTable,
      sortBy,
      isSortDesc,
      currentTableColumns,
      fetchData,
      refetchData,
      showInterestContentEdit,
      interestContentEdit,
      interestContentIsNew,
      interestContentItem,
      selectedContentIds,
      isEditMode,
      toggleEditMode,
    }
  },
}
</script>

<style scoped>
.content-label {
  font-size: inherit;
  display: block;
  width: 100%;
  cursor: pointer;
}
</style>
